import { Formik, FastField, Form, FormikHelpers } from 'formik';
import TechnologiesDropdown from '@/components/TechnologiesDropdown';
import Button from '@/trio-ui/Button';
import DropDown from '@/trio-ui/Dropdown';
import DropDownGroup from '@/trio-ui/DropdownGroup';
import InputText from '@/trio-ui/InputText';
import { ParsedAllStatusResponseType } from '@/types';
import { candidateSchema } from '@/utils/candidate';
import {
  englishLevelOptions,
  sourceOptions,
  yearsOfExperienceOptions,
  countriesOptions,
  developerTypeOptions,
  yesOrNoOptions,
} from '@/utils/dropdownOptions';
import { buildErrorMessage } from '@/utils/formsValidation';
import * as Styles from './NewCandidate.styles';

export const defaultValues = {
  firstName: '',
  lastName: '',
  email: '',
  source: sourceOptions[0],
  githubUrl: '',
  status: 'PENDING',
  mobilePhone: '',
  country: '',
  developerType: '',
  mainTechStack: [],
  linkedinUrl: '',
  englishLevel: '',
  experience: '',
  salaryExpectation: '',
  isTalentPool: 'No',
  referredBy: '',
  hasFintechExperience: 'No',
};

export type ValuesType = typeof defaultValues;

export type NewCandidateType = {
  candidateStatuses: ParsedAllStatusResponseType[];
  scrollToFieldWithError: () => void;
  onDirtyForm: (isDirty: boolean) => void;
  getUserName: (values: ValuesType) => string;
  getUserInitials: (values: ValuesType) => string;
  onHideCandidateFormClick: () => void;
  isFormDisabled?: boolean;
  onSubmitForm: (values: ValuesType, actions: FormikHelpers<any>) => void;
};

const NewCandidateComponent = function ({
  candidateStatuses,
  scrollToFieldWithError,
  getUserInitials,
  onDirtyForm,
  getUserName,
  onHideCandidateFormClick,
  isFormDisabled,
  onSubmitForm,
  ...props
}: NewCandidateType): JSX.Element {
  return (
    <div data-testid="new-candidate-form" {...props}>
      <Formik
        validationSchema={candidateSchema}
        initialValues={defaultValues}
        onSubmit={(e, a) => onSubmitForm(e, a)}
      >
        {(form) => {
          onDirtyForm(form.dirty);
          const isExternalSourced = ['Employee Referral', 'External Referral'].includes(
            form?.values?.source
          );
          return (
            <Form>
              <div>
                <Styles.PageHeader>
                  <Styles.Initial data-testid="new-candidate-initials">
                    {getUserInitials(form.values)}
                  </Styles.Initial>
                  <Styles.PageTitle data-testid="new-candidate-username">
                    {getUserName(form.values)}
                  </Styles.PageTitle>
                </Styles.PageHeader>
                <Styles.Box className="new-candidate-form-area">
                  <Styles.Field>
                    <FastField name="firstName">
                      {({ field }) => {
                        return (
                          <InputText
                            isLabelAboveField
                            onChangeHandler={form.handleChange}
                            label="First Name"
                            name="firstName"
                            placeholder="New"
                            errorMessage={buildErrorMessage(
                              form.touched.firstName,
                              form.errors.firstName
                            )}
                            value={field.value}
                          />
                        );
                      }}
                    </FastField>
                  </Styles.Field>
                  <FastField name="lastName">
                    {({ field }) => {
                      return (
                        <Styles.Field>
                          <InputText
                            isLabelAboveField
                            placeholder="Candidate"
                            onChangeHandler={form.handleChange}
                            label="Last Name"
                            name="lastName"
                            errorMessage={buildErrorMessage(
                              form.touched.lastName,
                              form.errors.lastName
                            )}
                            value={field.value}
                          />
                        </Styles.Field>
                      );
                    }}
                  </FastField>
                  <FastField name="email">
                    {({ field }) => {
                      return (
                        <Styles.Field>
                          <InputText
                            isLabelAboveField
                            onChangeHandler={form.handleChange}
                            label="E-mail"
                            name="email"
                            errorMessage={buildErrorMessage(form.touched.email, form.errors.email)}
                            value={field.value}
                          />
                        </Styles.Field>
                      );
                    }}
                  </FastField>
                  <FastField name="mobilePhone">
                    {({ field }) => {
                      return (
                        <Styles.Field>
                          <InputText
                            isLabelAboveField
                            onChangeHandler={form.handleChange}
                            label="Mobile Phone"
                            name="mobilePhone"
                            type="number"
                            errorMessage={buildErrorMessage(
                              form.touched.mobilePhone,
                              form.errors.mobilePhone
                            )}
                            value={field.value}
                          />
                        </Styles.Field>
                      );
                    }}
                  </FastField>
                  <FastField name="country">
                    {({ field }) => {
                      return (
                        <Styles.Field>
                          <DropDown
                            isLabelAboveField
                            options={countriesOptions}
                            name="country"
                            defaultValue={field.value}
                            label="Country"
                            errorMessage={buildErrorMessage(
                              form.touched.country,
                              form.errors.country
                            )}
                            onChangeDropDown={form.handleChange}
                          />
                        </Styles.Field>
                      );
                    }}
                  </FastField>
                  <FastField name="githubUrl">
                    {({ field }) => {
                      return (
                        <Styles.Field>
                          <InputText
                            isLabelAboveField
                            onChangeHandler={form.handleChange}
                            label="Github URL"
                            name="githubUrl"
                            errorMessage={buildErrorMessage(
                              form.touched.githubUrl,
                              form.errors.githubUrl
                            )}
                            value={field.value}
                          />
                        </Styles.Field>
                      );
                    }}
                  </FastField>
                  <FastField name="linkedinUrl">
                    {({ field }) => {
                      return (
                        <Styles.Field>
                          <InputText
                            isLabelAboveField
                            onChangeHandler={form.handleChange}
                            label="LinkedIn URL"
                            name="linkedinUrl"
                            errorMessage={form.touched.linkedinUrl ? form.errors.linkedinUrl : ''}
                            value={field.value}
                          />
                        </Styles.Field>
                      );
                    }}
                  </FastField>
                  <FastField name="developerType">
                    {({ field }) => {
                      return (
                        <Styles.Field>
                          <DropDown
                            isLabelAboveField
                            options={developerTypeOptions}
                            name="developerType"
                            errorMessage={buildErrorMessage(
                              form.touched.developerType,
                              form.errors.developerType
                            )}
                            defaultValue={field.value}
                            label="Developer Type"
                            onChangeDropDown={form.handleChange}
                          />
                        </Styles.Field>
                      );
                    }}
                  </FastField>

                  <FastField name="mainTechStack">
                    {({ field }) => {
                      const limitOptions = form.values.developerType.includes('Full Stack') ? 6 : 3;

                      return (
                        <Styles.Field>
                          <TechnologiesDropdown
                            isMulti
                            label="Main Tech Stack"
                            name="mainTechStack"
                            isOptionDisabled={() => field.value.length >= limitOptions}
                            onChange={form.handleChange}
                            errorMessage={buildErrorMessage(
                              form.touched.mainTechStack,
                              form.errors.mainTechStack
                            )}
                            defaultValue={field.value}
                          />
                        </Styles.Field>
                      );
                    }}
                  </FastField>

                  <FastField name="experience">
                    {({ field }) => {
                      return (
                        <Styles.Field>
                          <DropDown
                            isLabelAboveField
                            options={yearsOfExperienceOptions}
                            name="experience"
                            defaultValue={field.value}
                            label="Years of Experience"
                            errorMessage={buildErrorMessage(
                              form.touched.experience,
                              form.errors.experience
                            )}
                            onChangeDropDown={form.handleChange}
                          />
                        </Styles.Field>
                      );
                    }}
                  </FastField>
                  <FastField name="englishLevel">
                    {({ field }) => {
                      return (
                        <Styles.Field>
                          <DropDown
                            isLabelAboveField
                            optionsWithLabel={englishLevelOptions}
                            name="englishLevel"
                            defaultValue={field.value}
                            label="English Level"
                            errorMessage={buildErrorMessage(
                              form.touched.englishLevel,
                              form.errors.englishLevel
                            )}
                            onChangeDropDown={form.handleChange}
                          />
                        </Styles.Field>
                      );
                    }}
                  </FastField>
                  <FastField name="status">
                    {({ field }) => {
                      return (
                        <Styles.Field>
                          <DropDownGroup
                            isLabelAboveField
                            dropdownGroups={candidateStatuses}
                            name="status"
                            defaultValue={field.value}
                            label="Candidate status"
                            errorMessage={buildErrorMessage(
                              form.touched.status,
                              form.errors.status
                            )}
                            onChangeDropDown={form.handleChange}
                          />
                        </Styles.Field>
                      );
                    }}
                  </FastField>
                  <FastField name="source">
                    {({ field }) => {
                      return (
                        <Styles.Field>
                          <DropDown
                            isLabelAboveField
                            options={sourceOptions}
                            name="source"
                            defaultValue={field.value}
                            label="Candidate Source"
                            errorMessage={buildErrorMessage(
                              form.touched.source,
                              form.errors.source
                            )}
                            onChangeDropDown={form.handleChange}
                          />
                        </Styles.Field>
                      );
                    }}
                  </FastField>
                  {isExternalSourced && (
                    <FastField name="referredBy">
                      {({ field }) => {
                        return (
                          <Styles.Field>
                            <InputText
                              isLabelAboveField
                              name="referredBy"
                              value={field.value}
                              label="Referred By"
                              errorMessage={buildErrorMessage(
                                form.touched.referredBy,
                                form.errors.referredBy
                              )}
                              onChangeHandler={form.handleChange}
                            />
                          </Styles.Field>
                        );
                      }}
                    </FastField>
                  )}
                  <FastField name="isTalentPool">
                    {({ field }) => {
                      return (
                        <Styles.Field>
                          <DropDown
                            isLabelAboveField
                            options={yesOrNoOptions}
                            name="isTalentPool"
                            defaultValue={field.value}
                            label="Talent Pool"
                            errorMessage={buildErrorMessage(
                              form.touched.isTalentPool,
                              form.errors.isTalentPool
                            )}
                            onChangeDropDown={form.handleChange}
                          />
                        </Styles.Field>
                      );
                    }}
                  </FastField>
                  <FastField name="hasFintechExperience">
                    {({ field }) => {
                      return (
                        <Styles.Field>
                          <DropDown
                            isLabelAboveField
                            options={yesOrNoOptions}
                            name="hasFintechExperience"
                            defaultValue={field.value}
                            label="Fintech Experience"
                            errorMessage={buildErrorMessage(
                              form.touched.hasFintechExperience,
                              form.errors.hasFintechExperience
                            )}
                            onChangeDropDown={form.handleChange}
                          />
                        </Styles.Field>
                      );
                    }}
                  </FastField>
                  <Styles.Footer>
                    <Button
                      onClick={() => {
                        form.validateForm().then(scrollToFieldWithError);
                      }}
                      disabled={form.isSubmitting || isFormDisabled}
                      data-testid="new-candidate-submit-btn"
                      type="submit"
                    >
                      Save
                    </Button>
                    <Button
                      disabled={form.isSubmitting || isFormDisabled}
                      variantStyle="outline"
                      data-testid="new-candidate-cancel-btn"
                      onClick={(event) => {
                        event.stopPropagation();
                        form.resetForm();
                        onHideCandidateFormClick();
                      }}
                    >
                      Cancel
                    </Button>
                  </Styles.Footer>
                </Styles.Box>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default NewCandidateComponent;
